import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  mockCategories,
  mockMenuProducts,
  type MenuCategory,
  type MenuProduct,
} from "@/mocks/branch/menu";

interface MenuContextValue {
  categories: MenuCategory[];
  products: MenuProduct[];
  toggleAvailable: (id: string) => void;
  setQuantity: (id: string, qty: number | null) => void;
  markOutOfStock: (id: string) => void;
  disableAll: () => void;
  enableAll: () => void;
}

const MenuContext = createContext<MenuContextValue | undefined>(undefined);

function normalize(products: MenuProduct[]): MenuProduct[] {
  return products.map((p) => ({ ...p, available: p.available ?? true }));
}

export function MenuProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<MenuProduct[]>(() =>
    normalize(mockMenuProducts),
  );

  const toggleAvailable = useCallback((id: string) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, available: !p.available } : p)),
    );
  }, []);

  const setQuantity = useCallback((id: string, qty: number | null) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, quantity: qty } : p)),
    );
  }, []);

  const markOutOfStock = useCallback((id: string) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, available: false } : p)),
    );
  }, []);

  const disableAll = useCallback(() => {
    setProducts((prev) => prev.map((p) => ({ ...p, available: false })));
  }, []);

  const enableAll = useCallback(() => {
    setProducts((prev) => prev.map((p) => ({ ...p, available: true })));
  }, []);

  const value = useMemo<MenuContextValue>(
    () => ({
      categories: mockCategories,
      products,
      toggleAvailable,
      setQuantity,
      markOutOfStock,
      disableAll,
      enableAll,
    }),
    [products, toggleAvailable, setQuantity, markOutOfStock, disableAll, enableAll],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function useMenu(): MenuContextValue {
  const ctx = useContext(MenuContext);
  if (!ctx) {
    throw new Error("useMenu must be used within a MenuProvider");
  }
  return ctx;
}